/**
 * Native audio helpers.
 *
 * Purpose: when the app is backgrounded (call, screen off, home button) or
 * an interruption happens, HTML5 <audio> in a WKWebView / Chrome custom-tab
 * gets paused by the OS but does NOT restart automatically when the app
 * returns to the foreground. That leaves users staring at a silent
 * visualisation or breathwork session.
 *
 * We listen for two signals:
 *   1. `visibilitychange` — works on both web and native WebViews.
 *   2. `fs:app-active` / `fs:app-inactive` — dispatched by native.js from
 *      Capacitor App plugin's `appStateChange`, which fires reliably on
 *      Android (visibilitychange is unreliable there).
 *
 * The hook records which audio refs were playing on suspend and resumes
 * them on resume. Callers pass a `getRefs()` fn so we always see the
 * latest refs, plus a `wasPlaying()` predicate.
 *
 * All helpers safely no-op if there's nothing to do.
 */

import { useEffect } from 'react';

/**
 * Attach interruption handling to a list of HTMLAudioElement refs.
 *
 * @param {{
 *   getRefs: () => Array<{ current: HTMLMediaElement | null }>,
 *   isSessionActive: () => boolean,   // only auto-resume if user hadn't paused
 * }} opts
 */
export const useAudioInterruption = ({ getRefs, isSessionActive }) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Track which refs we paused, so we only resume those (not ones the
    // user had already paused manually before the interruption).
    let pausedByUs = [];

    const suspend = () => {
      if (!isSessionActive?.()) return;
      pausedByUs = [];
      for (const ref of getRefs() || []) {
        const el = ref?.current;
        if (el && !el.paused) {
          try { el.pause(); pausedByUs.push(ref); } catch { /* noop */ }
        }
      }
    };

    const resume = () => {
      if (!isSessionActive?.()) { pausedByUs = []; return; }
      for (const ref of pausedByUs) {
        const el = ref?.current;
        if (el) {
          // play() returns a promise on modern browsers; swallow rejections
          // so a single failure doesn't stop the rest from resuming.
          try { const p = el.play(); if (p && p.catch) p.catch(() => {}); } catch { /* noop */ }
        }
      }
      pausedByUs = [];
    };

    const onVisibility = () => {
      if (document.hidden) suspend(); else resume();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('fs:app-inactive', suspend);
    window.addEventListener('fs:app-active', resume);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('fs:app-inactive', suspend);
      window.removeEventListener('fs:app-active', resume);
    };
    // getRefs / isSessionActive are expected to be stable refs from the
    // caller (they read live state via closures) — we intentionally do
    // not re-subscribe on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
